import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import FooterSection from '../components/FooterSection';
import FeaturedCategories from '../components/FeaturedCategories';
import SEO from '../components/SEO';
import FoodImage from '../components/FoodImage';
import { LanguageProvider } from '../context/LanguageContext';
import { findCategory, CATEGORIES, matchCategoryFromCuisine } from '../data/categories';
import { ArrowLeft, MapPin, Star, ChefHat, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RestaurantTile = ({ r }) => (
 <Link
 to={`/menu/${r.slug}`}
 data-testid={`category-restaurant-${r.slug}`}
 className="group rounded-2xl overflow-hidden border border-white/10 bg-zinc-950 hover:border-gold-500/40 transition-all hover:-translate-y-0.5"
 >
 <div className="aspect-[16/9] bg-gradient-to-br from-zinc-900 to-black relative overflow-hidden">
 {r.cover_url? (
 <FoodImage
 src={r.cover_url}
 alt={r.name}
 variant="card"
 className="absolute inset-0 w-full h-full group-hover:scale-105 transition-transform duration-700"
 />
 ): (
 <div className="w-full h-full flex items-center justify-center text-4xl">
 {findCategory(matchCategoryFromCuisine(r.cuisine_type)?.id || '')?.emoji || '🍽️'}
 </div>
 )}
 </div>
 <div className="p-4">
 <h3 className="text-[15px] font-black text-white truncate group-hover:text-gold-200">
 {r.name}
 </h3>
 <div className="mt-1 flex items-center gap-2 text-[12px] text-white/55">
 {r.cuisine_type && <span className="capitalize">{r.cuisine_type}</span>}
 {r.rating!= null && (
 <span className="inline-flex items-center gap-0.5">
 <Star className="w-3 h-3 text-amber-400" fill="currentColor" />
 {Number(r.rating).toFixed(1)}
 </span>
 )}
 </div>
 </div>
 </Link>
);

const CategoryPage = () => {
 const { slug } = useParams();
 const category = findCategory(slug);
 const [loading, setLoading] = useState(true);
 const [restaurants, setRestaurants] = useState([]);

 useEffect(() => {
 if (!category) {
 setLoading(false);
 return;
 }
 setLoading(true);
 (async () => {
 try {
 // Pull a broad set, then filter client-side using fuzzy aliases.
 const r = await axios.get(`${API}/public/restaurants`, { params: { limit: 200 } });
 const all = r.data?.restaurants || [];
 const filtered = all.filter((rest) => {
 const c = (rest.cuisine_type || '').toLowerCase();
 return category.aliases.some((a) => c.includes(a));
 });
 setRestaurants(filtered);
 } catch {
 setRestaurants([]);
 } finally {
 setLoading(false);
 }
 })();
 }, [slug, category]);

 if (!category) {
 return (
 <LanguageProvider>
 <div className="min-h-screen bg-black text-white">
 <SEO
 title="Category not found"
 description="Browse all food categories on NoFee — 0% commission, direct ordering from local restaurants."
 path={`/category/${slug}`}
 noindex
 />
 <Navbar />
 <div className="max-w-[900px] mx-auto px-4 pt-40 pb-24 text-center">
 <h1 className="text-3xl font-black tracking-tight">Category not found</h1>
 <p className="mt-2 text-white/55">
 The category "{slug}" doesn't exist. Browse all categories below.
 </p>
 <div className="mt-10">
 <FeaturedCategories title="Browse by category" />
 </div>
 </div>
 <FooterSection />
 </div>
 </LanguageProvider>
 );
 }

 return (
 <LanguageProvider>
 <div className="min-h-screen bg-black text-white">
 <SEO
 title={`${category.label} Restaurants — Order Online`}
 description={`Discover ${restaurants.length || 'local'} ${category.label.toLowerCase()} restaurants on NoFee. 0% commission · direct ordering · pay with Stripe.`}
 path={`/category/${slug}`}
 jsonLd={{
 "@context": "https://schema.org",
 "@type": "CollectionPage",
 "name": `${category.label} Restaurants on NoFee`,
 "url": `https://nofee.restaurant/category/${slug}`,
 "about": category.label,
 "mainEntity": {
 "@type": "ItemList",
 "numberOfItems": restaurants.length,
 "itemListElement": restaurants.slice(0, 20).map((r, i) => ({
 "@type": "ListItem",
 "position": i + 1,
 "url": `https://nofee.restaurant/menu/${r.slug}`,
 "name": r.name,
 })),
 },
 }}
 />
 <Navbar />

 {/* Hero */}
 <section className="relative pt-28 sm:pt-32 lg:pt-40 pb-10 lg:pb-14">
 <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(212,175,55,0.10),transparent_60%)]" />
 <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
 <Link
 to="/restaurants"
 className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-gold-300 transition-colors mb-6"
 data-testid="category-back-link"
 >
 <ArrowLeft className="w-3.5 h-3.5" />
 All restaurants
 </Link>

 <div className="flex items-center gap-4">
 <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-3xl bg-gold-500/10 border border-gold-500/25 flex items-center justify-center text-4xl sm:text-5xl">
 {category.emoji}
 </div>
 <div>
 <p className="text-[11px] uppercase tracking-[0.22em] text-gold-400 font-bold">
 {category.group}
 </p>
 <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">
 {category.label}
 </h1>
 </div>
 </div>

 <p className="mt-4 text-base text-white/60 max-w-2xl">
 {restaurants.length > 0
? `${restaurants.length} restaurant${restaurants.length === 1? '': 's'} on NoFee serving ${category.label.toLowerCase()}.`
: `No restaurants yet on NoFee for ${category.label.toLowerCase()}. Check back soon, or browse other categories below.`}
 </p>
 </div>
 </section>

 {/* Restaurant grid */}
 <section className="relative pb-16 lg:pb-20">
 <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
 {loading? (
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
 {[0, 1, 2, 3, 4, 5].map((i) => (
 <div key={i} className="aspect-[16/9] rounded-2xl bg-white/[0.03] animate-pulse" />
 ))}
 </div>
 ): restaurants.length > 0? (
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
 {restaurants.map((r) => <RestaurantTile key={r.id} r={r} />)}
 </div>
 ): (
 <div className="rounded-3xl border border-white/10 bg-zinc-950 p-10 text-center">
 <ChefHat className="w-10 h-10 text-white/20 mx-auto mb-3" />
 <p className="text-white/55">
 No NoFee restaurants in this category yet. Be the first — invite your favorite {category.label.toLowerCase()} spot.
 </p>
 <Link
 to="/for-restaurants"
 className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-bold text-gold-300 hover:text-gold-200"
 >
 Invite a restaurant →
 </Link>
 </div>
 )}
 </div>
 </section>

 {/* Other categories */}
 <section className="relative pb-24 border-t border-white/[0.05]">
 <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pt-16">
 <FeaturedCategories
 title="Explore more"
 subtitle="Other tastes"
 />
 </div>
 </section>

 <FooterSection />
 </div>
 </LanguageProvider>
 );
};

export default CategoryPage;
